'use strict';
import classNames from 'classnames';
import get from 'lodash/get';
import * as T from 'prop-types';
import React, {Fragment} from 'react';
import {CommentIndicator} from '../../../comments/components/CommentIndicator';
import {Button} from '../../../components/buttons/Button';
import {QuickStatusButton} from '../../../components/buttons/QuickStatusButton';
import {LabeledBlock} from '../../../components/forms/LabeledBlock';
import {Icon} from '../../../components/Icon';
import {StatelessStatusSelector} from '../../../core-components/selectors/components/StatelessStatusSelector';
import {getAsString, getCollection, getId} from '../../../core-utilities/domain/ReduxUtils';
import {isButtonState} from '../../../core-utilities/enums/ButtonState';
import {getMessagesFromProps, I18nComponent} from '../../../core-utilities/react-i18n';
import {PeopleItem} from '../../../personnel/components/PeopleItem';
import {TaskInfoTimer} from '../../../timers/components/TaskInfoTimer';
import {isTaskType, TaskType} from '../../enums/TaskType';
import {TaskAssignmentButton} from '../TaskAssignmentButton';
import {TaskDate} from '../TaskDate';
import {TaskDateField} from '../TaskDateField';


export function BaseTaskViewBannerView(props) {
  const isTemplate = props.taskType === TaskType.TEMPLATE;
  const task = props.task;
  const description = get(task, 'description');

  return (
    <div className={classNames("TaskViewBanner", props.className)}>
      <div className="TaskViewBanner__header">
        <h1 className="TaskViewBanner__headerLabel">
          {getAsString(task)}
        </h1>

        <div className="TaskViewBanner__statusSection">
          {isTemplate ? null : (
            <Fragment>
              <span className="TaskViewBanner__statusLabel">
                {`${props.statusLabel}:`}
              </span>
              {task ? (
                <StatelessStatusSelector
                  className="TaskViewBanner__status"
                  labelClassName="TaskViewBanner__status__label"
                  model={task}
                  hideValue={false}
                  hideIcon={true}

                  status={task.status}
                  collection={task.collection}
                  validStatuses={task.validStatuses}
                />
              ) : null}
              <CommentIndicator
                className="TaskViewBanner__chat"
                titleClassName="TaskViewBanner__chatTitle"
                displayTitle={true}
                title={props.chatTitle}
                userId={props.userId}
                chatId={getId(task)}
                collection={getCollection(task)}
                allowNav={false}

                init={props.init}
                {...getMessagesFromProps(props, CommentIndicator.messageProps)}
              />
            </Fragment>
          )}
          <Button
            className="TaskViewBanner__editButton"
            label={props.editLabel}
            onClick={props.onClickEdit}
          />
        </div>
        <div className="TaskViewBanner__wrapper">
          {isTemplate ? null : (
            <div className="TaskViewBanner__schedulePanel">
              {props.showScheduled ? (
                <LabeledBlock
                  className="TaskViewBanner__schedule"
                  label={props.scheduledLabel}
                >
                  <TaskAssignmentButton
                    className="TaskViewBanner__assignmentButton"
                    model={task}
                    allowNav={false}
                  >
                    <TaskDate
                      date={get(task, 'scheduled')}
                      useTime={get(task, 'useTime')}
                      placeholder={props.notScheduledLabel}
                    />
                  </TaskAssignmentButton>
                </LabeledBlock>
              ) : null}

              {props.showSubmitted ? (
                <Fragment>
                  <Icon
                    className="TaskViewBanner__scheduleDivider"
                    icon="ion-chevron-right"
                  />
                  <TaskDateField
                    className="TaskViewBanner__schedule"
                    label="status.submitted.label"
                    date={get(task, 'submittedOn')}
                    useTime={true}
                  />
                </Fragment>
              ) : null}

              {props.showApproved ? (
                <Fragment>
                  <Icon
                    className="TaskViewBanner__scheduleDivider"
                    icon="ion-chevron-right"
                  />
                  <TaskDateField
                    className="TaskViewBanner__schedule"
                    label="tasks.schedule.completedate.label"
                    date={get(task, 'approvedOn')}
                    useTime={true}
                  />
                </Fragment>
              ) : null}

              {props.showCancelled ? (
                <Fragment>
                  <Icon
                    className="TaskViewBanner__scheduleDivider"
                    icon="ion-chevron-right"
                  />
                  <TaskDateField
                    className="TaskViewBanner__schedule"
                    label="status.cancelled.label"
                    date={get(task, 'cancelledOn')}
                    useTime={true}
                  />
                </Fragment>
              ) : null}

              {props.showAssignment ? (
                <LabeledBlock
                  className="TaskViewBanner__assignedTo"
                  label={props.assignedToLabel}
                >
                  <TaskAssignmentButton
                    className="TaskViewBanner__assignmentButton"
                    model={task}
                    allowNav={false}
                  >
                    {get(task, 'assignedTo') ? (
                      <PeopleItem
                        className="TaskViewBanner__assignedTo__person"
                        isSimple={true}
                        model={task.assignedTo}
                        nullLabel="tasks.properties.assignedTo.null.message"
                        avatarImageWidth={16}
                        avatarImageHeight={16}

                        init={props.init}
                      />
                    ) : (
                      props.unassignedLabel
                    )}
                  </TaskAssignmentButton>
                </LabeledBlock>
              ) : null}
            </div>
          )}

          {description && (
            <Fragment>
              <span className="LabeledBlock__label">
                Description
              </span>
              <div className="TaskViewBanner__descriptionWrapper">
                <span className={classNames("TaskViewBanner__description", {
                  TaskViewBanner__description_collapsed: !props.isHeaderExpanded,
                  TaskViewBanner__description_expanded: props.isHeaderExpanded,
                })}
                >
                  {description}
                </span>

                <Button
                  className="TaskViewBanner__expandButton"
                  label={props.isHeaderExpanded ? props.collapseLabel : props.expandLabel}
                  onClick={props.onToggleHeaderExpanded}
                />
              </div>
            </Fragment>
          )}
        </div>

        <div className="TaskViewBanner__timerWrapper">
          {isTemplate ? null : (
            <TaskInfoTimer
              className="TaskViewBanner__timer"
              task={props.task}
              parentName={props.parentName}
            />
          )}
        </div>
      </div>

      <div className="TaskViewBanner__headerSubPanel">
        <QuickStatusButton
          className="TaskViewBanner__quickButton"
          model={task}
          status={get(task, 'status')}
          validStatuses={get(task, 'validStatuses')}
        />
        <Button
          className="TaskViewBanner__addReqButton"
          label={props.addFileReqLabel}
          disabled={!props.canAddReq}
          onClick={props.onAddFileRequirement}
        />
        <Button
          className="TaskViewBanner__addReqButton"
          label={props.addFormReqLabel}
          disabled={!props.canAddReq}
          onClick={props.onAddFormRequirement}
        />
        {props.supportsDownload ? (
          <Button
            className="TaskViewBanner__downloadButton"
            label={props.downloadTitle}
            disabled={!props.canDownloadFiles}
            onClick={props.onDownloadAllFiles}
          />
        ) : null}
        <Button
          className="TaskViewBanner__toggleMinimalView"
          label={props.isMinimalView ? props.listExpandLabel : props.listCollapseLabel}
          onClick={props.onToggleMinimalView}
        />
      </div>
    </div>
  );
}

BaseTaskViewBannerView.propTypes = {
  className: T.string,
  isHeaderExpanded: T.bool,
  userId: T.string.isRequired,
  taskType: isTaskType.isRequired,
  task: T.object,
  parentName: T.string,

  canAddReq: T.bool,
  supportsDownload: T.bool,
  canDownloadFiles: T.bool,
  showQuickSubmit: T.bool,
  showQuickApprove: T.bool,
  quickMode: isButtonState,

  onClickEdit: T.func.isRequired,
  onToggleHeaderExpanded: T.func.isRequired,
  onToggleMinimalView: T.func.isRequired,
  onAddFileRequirement: T.func.isRequired,
  onAddFormRequirement: T.func.isRequired,
  onDownloadAllFiles: T.func.isRequired,

  statusLabel: T.string,
  editLabel: T.string,
  addFileReqLabel: T.string,
  addFormReqLabel: T.string,
  downloadTitle: T.string,
  rejectedReqCount: T.number,
  defaultAssignmentLabel: T.string,
  timersHeaderLabel: T.string,
  scheduledLabel: T.string,
  submittedLabel: T.string,
  approvedLabel: T.string,
  cancelledLabel: T.string,
  assignedToLabel: T.string,
  unassignedLabel: T.string,
  notScheduledLabel: T.string,
  collapseLabel: T.string,
  expandLabel: T.string,
  listCollapseLabel: T.string,
  listExpandLabel: T.string,

  init: T.bool,
};

BaseTaskViewBannerView.defaultProps = {
  init: true,
};

export const TaskViewBannerView = I18nComponent(BaseTaskViewBannerView, [
  {code: 'status.label', prop: 'statusLabel'},
  {code: 'default.button.edit.label', prop: 'editLabel'},
  {code: 'tasks.details.context.addreq.file.label', prop: 'addFileReqLabel'},
  {code: 'tasks.details.context.addreq.form.label', prop: 'addFormReqLabel'},
  {code: 'tasks.icon.download.title.short', prop: 'downloadTitle'},
  {code: 'tasks.default.assignment.label', prop: 'defaultAssignmentLabel'},
  {code: 'timers.header.label', prop: 'timersHeaderLabel'},
  {code: 'schedule.scheduled.label', prop: 'scheduledLabel'},
  {code: 'schedule.submitted.label', prop: 'submittedLabel'},
  {code: 'tasks.schedule.completedate.label', prop: 'approvedLabel'},
  {code: 'schedule.cancelled.label', prop: 'cancelledLabel'},
  {code: 'tasks.schedule.assignedto.label', prop: 'assignedToLabel'},
  {code: 'tasks.properties.assignedTo.null.message', prop: 'unassignedLabel'},
  {code: 'schedule.not_scheduled.label', prop: 'notScheduledLabel'},
  {code: 'default.button.collapse.label', prop: 'collapseLabel'},
  {code: 'default.button.expand.label', prop: 'expandLabel'},
  {code: 'requirements.item.collapse.label', prop: 'listCollapseLabel'},
  {code: 'requirements.item.expand.label', prop: 'listExpandLabel'},
  {code: 'tasks.views.chat.title', prop: 'chatTitle'},
  ...CommentIndicator.messages,
]);



// WEBPACK FOOTER //
// ./src/js/tasks/components/TaskViewBanner/TaskViewBannerView.js